import { create } from 'zustand';
import type { ModalId, NavId, NotificationItem, ScreenId } from '@/types';

type ModalPayload = Record<string, unknown> | undefined;

interface ModalState {
  id: ModalId;
  payload?: ModalPayload;
}

interface UIState {
  currentScreen: ScreenId;
  setScreen: (s: ScreenId) => void;

  activeNav: NavId | null;
  setActiveNav: (n: NavId | null) => void;

  selectedWaifuId: number | null;
  setSelectedWaifuId: (id: number | null) => void;

  openModals: ModalState[];
  openModal: (id: ModalId, payload?: ModalPayload) => void;
  closeModal: (id: ModalId) => void;
  closeAllModals: () => void;
  isModalOpen: (id: ModalId) => boolean;

  notifications: NotificationItem[];
  pushNotification: (n: Omit<NotificationItem, 'id'> & { id?: string }) => string;
  dismissNotification: (id: string) => void;

  globalLoading: boolean;
  globalLoadingText: string | null;
  setGlobalLoading: (loading: boolean, text?: string | null) => void;

  archivePage: number;
  setArchivePage: (page: number) => void;
}

const screenToNav: Partial<Record<ScreenId, NavId>> = {
  main: 'world',
  waifus: 'waifus',
  journal: 'journal',
  'player-menu': 'player-menu',
  achievements: 'achievements',
  settings: 'settings',
};

export const useUIStore = create<UIState>((set, get) => ({
  currentScreen: 'loading',
  setScreen: (s) => set({ currentScreen: s, activeNav: screenToNav[s] ?? get().activeNav }),

  activeNav: null,
  setActiveNav: (n) => set({ activeNav: n }),

  selectedWaifuId: null,
  setSelectedWaifuId: (id) => set({ selectedWaifuId: id }),

  openModals: [],
  openModal: (id, payload) =>
    set((state) => {
      if (state.openModals.some((m) => m.id === id)) return state;
      return { openModals: [...state.openModals, { id, payload }] };
    }),
  closeModal: (id) =>
    set((state) => ({ openModals: state.openModals.filter((m) => m.id !== id) })),
  closeAllModals: () => set({ openModals: [] }),
  isModalOpen: (id) => get().openModals.some((m) => m.id === id),

  notifications: [],
  pushNotification: (n) => {
    const id = n.id ?? Math.random().toString(36).slice(2);
    set((state) => ({
      notifications: [...state.notifications, { ...n, id }],
    }));
    return id;
  },
  dismissNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((it) => it.id !== id),
    })),

  globalLoading: false,
  globalLoadingText: null,
  setGlobalLoading: (loading, text = null) =>
    set({ globalLoading: loading, globalLoadingText: loading ? text : null }),

  archivePage: 1,
  setArchivePage: (page) => set({ archivePage: page }),
}));

export function useModalPayload<T = ModalPayload>(id: ModalId): T | undefined {
  return useUIStore((s) => s.openModals.find((m) => m.id === id)?.payload) as T | undefined;
}
